import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { useLabelCurral } from '../../hooks/useLabelCurral';
import { lotesService } from '../../services/lotesService';
import leiloesService from '../../services/leiloesService';
import { contratoService } from '../../services/contratoService';
import './contrato.css';

const ContratoUmaVia = () => {
  const { loteId } = useParams();
  const { organizadorAtivo } = useOrganizador();
  const labelCurral = useLabelCurral();
  const [lote, setLote] = useState(null);
  const [leilao, setLeilao] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    if (loteId && organizadorAtivo?.id) {
      carregarDados();
    }
  }, [loteId, organizadorAtivo]);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const loteData = await lotesService.getById(organizadorAtivo.id, loteId);
      const [leilaoData, configData] = await Promise.all([
        leiloesService.getById(organizadorAtivo.id, loteData.leilao_id),
        contratoService.getConfig(organizadorAtivo.id)
      ]);
      
      setLote(loteData);
      setLeilao(leilaoData);
      setConfig(configData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    if (printRef.current) {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      printWindow.document.write(`
        <html>
          <head>
            <title>Contrato de Compra e Venda - Lote ${lote.numero_lote}</title>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
              ${getContratoCSS()}
            </style>
          </head>
          <body>
            ${printRef.current.innerHTML}
          </body>
        </html>
      `);
      
      printWindow.document.close();
      
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
          setTimeout(() => printWindow.close(), 500);
        }, 500);
      };
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(2)}%`;
  };

  const numeroExtenso = (valor) => {
    const unidades = ['', 'um', 'dois', 'três', 'quatro', 'cinco', 'seis', 'sete', 'oito', 'nove'];
    const especiais = ['dez', 'onze', 'doze', 'treze', 'quatorze', 'quinze', 'dezesseis', 'dezessete', 'dezoito', 'dezenove'];
    const dezenas = ['', '', 'vinte', 'trinta', 'quarenta', 'cinquenta', 'sessenta', 'setenta', 'oitenta', 'noventa'];
    const centenas = ['', 'cento', 'duzentos', 'trezentos', 'quatrocentos', 'quinhentos', 'seiscentos', 'setecentos', 'oitocentos', 'novecentos'];

    const tres = (n) => {
      if (n === 0) return '';
      if (n === 100) return 'cem';
      const c = Math.floor(n / 100);
      const resto = n % 100;
      const d = Math.floor(resto / 10);
      const u = resto % 10;
      const parts = [];
      if (c) parts.push(centenas[c]);
      if (resto >= 10 && resto <= 19) {
        parts.push(especiais[resto - 10]);
      } else {
        if (d) parts.push(dezenas[d]);
        if (u) parts.push(unidades[u]);
      }
      return parts.join(' e ');
    };

    const inteiroExtenso = (n) => {
      if (n === 0) return 'zero';
      const partes = [];
      const milhoes = Math.floor(n / 1000000);
      n %= 1000000;
      const mil = Math.floor(n / 1000);
      n %= 1000;
      if (milhoes) partes.push(tres(milhoes) + (milhoes === 1 ? ' milhão' : ' milhões'));
      if (mil) partes.push(mil === 1 ? 'mil' : tres(mil) + ' mil');
      if (n) partes.push(tres(n));
      return partes.join(' e ');
    };

    valor = Number(valor) || 0;
    let inteiro = Math.floor(valor);
    let decimal = Math.round((valor - inteiro) * 100);
    if (decimal === 100) {
      inteiro += 1;
      decimal = 0;
    }
    let texto = inteiroExtenso(inteiro);
    if (decimal) texto += ' vírgula ' + tres(decimal);
    return texto;
  };

  const formatCPFCNPJ = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (numbers.length === 14) {
      return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  };

  const formatPhone = (value) => {
    if (!value) return '';
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 11) {
      return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (numbers.length === 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const formatText = (text, maxLength = null) => {
    if (!text) return '';
    const formatted = text.toString().toUpperCase();
    return maxLength ? formatted.substring(0, maxLength) : formatted;
  };

  const processarClausulas = (clausulas) => {
    const comissaoVenda = lote.comissao?.comissao_venda || 0;
    const comissaoCompra = lote.comissao?.comissao_compra || 0;
    const valorTotal = lote.valor_venda_total || 0;
    const valorComissaoVenda = (valorTotal * comissaoVenda) / 100;
    const valorComissaoCompra = (valorTotal * comissaoCompra) / 100;
    const valorTotalComComissao = valorTotal + valorComissaoCompra;
    const valorLiquido = valorTotal;

    return clausulas
      .replace(/%comissao_venda%/g, `${formatPercentage(comissaoVenda)} — ${formatCurrency(valorComissaoVenda)}`)
      .replace(/%comissao_compra%/g, `${formatPercentage(comissaoCompra)} — ${formatCurrency(valorComissaoCompra)}`)
      .replace(/%comissao_total%/g, formatPercentage(comissaoVenda + comissaoCompra))
      .replace(/%comissao_total_numero%/g, formatPercentage(comissaoVenda + comissaoCompra).replace('%', ''))
      .replace(/%comissao_total_extenso%/g, `${numeroExtenso(comissaoVenda + comissaoCompra)} por cento`)
      .replace(/%valor_total%/g, formatCurrency(valorTotal))
      .replace(/%valor_total_comissao_compra%/g, formatCurrency(valorTotalComComissao))
      .replace(/%valor_liquido%/g, formatCurrency(valorLiquido))
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n\n/g, '<div style="margin: 15px 0;"></div>')
      .replace(/\n/g, '<br>');
  };

  const getContratoCSS = () => {
    return `
      @page { 
        size: A4 portrait; 
        margin: 8mm 12mm 15mm 12mm; 
      }
      
      * { 
        margin: 0; 
        padding: 0; 
        box-sizing: border-box; 
      }
      
      body { 
        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
        font-size: 11px; 
        line-height: 1.5; 
        color: #2c3e50;
        background: white;
      }
      
      .contrato-container { 
        width: 100%; 
        max-width: 210mm; 
        background: white;
        position: relative;
      }
      
      .cabecalho { 
        display: flex; 
        justify-content: space-between; 
        align-items: center; 
        margin-bottom: 5px; 
        padding: 20px;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        border-radius: 8px;
        box-shadow: 0 4px 15px rgba(0,0,0,0.1);
      }
      
      .logo-esquerda, .logo-direita { 
        flex: 1; 
        display: flex;
        align-items: center;
      }
      
      .logo-direita { 
        justify-content: flex-end; 
      }
      
      .logo { 
        max-height: 50px; 
        max-width: 100px; 
        filter: brightness(0) invert(1);
      }
      
      .logo-rematei { 
        max-height: 35px; 
        max-width: 70px; 
        filter: brightness(0) invert(1);
      }
      
      .org-nome { 
        font-size: 10px; 
        font-weight: 600; 
        margin-top: 5px;
        opacity: 0.9;
      }
      
      .titulo-centro { 
        text-align: center; 
        flex: 2;
        padding: 0 20px;
      }
      
      .titulo-centro h1 { 
        font-size: 18px; 
        font-weight: 700; 
        margin-bottom: 8px;
        text-shadow: 0 1px 2px rgba(0,0,0,0.1);
      }
      
      .evento-info { 
        font-size: 12px; 
        font-weight: 500;
        opacity: 0.95;
      }
      
      table { 
        width: 100%; 
        border-collapse: collapse; 
        margin-bottom: 20px;
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      }
      
      th { 
        background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
        color: white;
        font-weight: 600;
        padding: 12px 8px;
        text-align: center;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      td { 
        border: 1px solid #e5e7eb; 
        padding: 10px 8px; 
        text-align: center;
        background: white;
        font-size: 10px;
      }
      
      tr:nth-child(even) td {
        background: #f9fafb;
      }
      
      .tabela-identificacao {
        border-radius: 8px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(0,0,0,0.08);
      }
      
      .tabela-identificacao th {
        background: linear-gradient(135deg, #1e40af 0%, #3730a3 100%);
        font-size: 10px;
        padding: 8px 6px;
      }
      
      .tabela-identificacao td {
        padding: 8px 6px;
        font-size: 9px;
        vertical-align: middle;
      }
      
      .tabela-comissoes {
        background: white;
      }
      
      .tabela-comissoes td:first-child { 
        text-align: left; 
        font-weight: 600;
        color: #374151;
      }
      
      .tabela-comissoes td:last-child { 
        text-align: right;
        font-weight: 600;
        color: #059669;
      }
      
      .tabela-parcelas th {
        background: linear-gradient(135deg, #059669 0%, #047857 100%);
      }
      
      .observacoes { 
        margin: 15px 0 5px 0;
        padding: 20px;
        background: #f8fafc;
        border-left: 4px solid #3b82f6;
        border-radius: 0 8px 8px 0;
      }
      
      .observacoes h3 { 
        font-size: 14px; 
        margin-bottom: 15px;
        color: #1e40af;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .clausulas { 
        text-align: justify; 
        line-height: 1.7;
        color: #374151;
        font-size: 10px;
      }
      
      .rodape { 
        margin-top: 40px;
        padding-top: 20px;
        border-top: 2px solid #e5e7eb;
      }
      
      .data-local { 
        text-align: center; 
        margin-bottom: 40px; 
        font-weight: 600;
        color: #374151;
        font-size: 12px;
      }
      
      .assinaturas { 
        display: flex; 
        justify-content: space-around; 
        margin-bottom: 30px;
        gap: 40px;
      }
      
      .assinatura { 
        text-align: center;
        flex: 1;
      }
      
      .linha { 
        width: 180px; 
        height: 2px; 
        background: linear-gradient(90deg, #9ca3af 0%, #374151 50%, #9ca3af 100%);
        margin: 0 auto 8px;
        border-radius: 1px;
      }
      
      .nome { 
        font-weight: 600;
        color: #374151;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .site { 
        text-align: center; 
        font-size: 9px; 
        color: #9ca3af;
        margin-top: 20px;
        padding-top: 15px;
        border-top: 1px solid #e5e7eb;
        font-weight: 500;
      }
    `;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!lote || !leilao || !config) {
    return <div>Dados não encontrados</div>;
  }

  const comissaoVenda = lote.comissao?.comissao_venda || 0;
  const comissaoCompra = lote.comissao?.comissao_compra || 0;
  const valorTotal = lote.valor_venda_total || 0;
  const valorComissaoVenda = (valorTotal * comissaoVenda) / 100;
  const valorComissaoCompra = (valorTotal * comissaoCompra) / 100;
  const somaComissoes = valorComissaoVenda + valorComissaoCompra;
  const valorTotalComComissao = valorTotal + valorComissaoCompra;
  const valorUnitario = valorTotal / lote.qtd;
  const descricao = `${formatText(lote.tipo_animal?.nome, 12)} ${formatText(lote.sexo, 8)} ${formatText(lote.idade?.nome, 8)}`;
  const peso = lote.peso ? `${lote.peso}kg` : '-';
  const raca = lote.raca?.nome ? formatText(lote.raca.nome, 12) : '-';
  const parcelas = lote.dados_pagamento?.parcelas_calculadas || [];

  return (
    <div className="print-area">
      <div className="flex justify-between items-center p-4 border-b bg-white">
        <h2 className="text-xl font-bold">Contrato 1 Via - Lote {lote.numero_lote}</h2>
        <div className="flex gap-2">
          <button
            onClick={handlePrint}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Imprimir
          </button>
          <button
            onClick={() => window.history.back()}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Voltar
          </button>
        </div>
      </div>

      <div ref={printRef}>
        <div className="contrato-container">
          {/* Cabeçalho */}
          <div className="cabecalho">
            <div className="logo-esquerda">
              {organizadorAtivo.logo_url ? (
                <div>
                  <img src={organizadorAtivo.logo_url} alt="Logo" className="logo" />
                  <div className="org-nome">{formatText(organizadorAtivo.nome, 25)}</div>
                </div>
              ) : (
                <div className="org-nome">{formatText(organizadorAtivo.nome, 25)}</div>
              )}
            </div>
            <div className="titulo-centro">
              <h1>CONTRATO DE COMPRA E VENDA</h1>
              <h1>COM RESERVA DE DOMÍNIO</h1>
              <div className="evento-info">
                <div>{formatText(leilao.nome, 40)}</div>
                <div>{formatDate(leilao.data_leilao)}</div>
              </div>
            </div>
            <div className="logo-direita">
              <div>
                <img src="/assets/rematei.png" alt="Rematei" className="logo-rematei" />
                <div className="org-nome">REMATEI</div>
              </div>
            </div>
          </div>

          {/* Bloco Identificação */}
          <table className="tabela-identificacao">
            <thead>
              <tr>
                <th style={{width: '15%'}}>Parte</th>
                <th style={{width: '25%'}}>Nome</th>
                <th style={{width: '15%'}}>CPF/CNPJ</th>
                <th style={{width: '20%'}}>Cidade/UF</th>
                <th style={{width: '15%'}}>Telefone</th>
                <th style={{width: '10%'}}>Fazenda</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong style={{color: '#2563eb'}}>VENDEDOR</strong></td>
                <td style={{textAlign: 'left', paddingLeft: '8px'}}>{formatText(lote.vendedor?.nome, 25)}</td>
                <td>{formatCPFCNPJ(lote.vendedor?.cpf_cnpj)}</td>
                <td>{formatText(lote.vendedor?.cidade, 15)}/{formatText(lote.vendedor?.estado, 2)}</td>
                <td>{formatPhone(lote.vendedor?.telefone)}</td>
                <td style={{textAlign: 'left', fontSize: '9px'}}>{formatText(lote.fazenda?.nome, 12)}</td>
              </tr>
              <tr>
                <td><strong style={{color: '#7c3aed'}}>COMPRADOR</strong></td>
                <td style={{textAlign: 'left', paddingLeft: '8px'}}>{formatText(lote.comprador?.nome, 25)}</td>
                <td>{formatCPFCNPJ(lote.comprador?.cpf_cnpj)}</td>
                <td>{formatText(lote.comprador?.cidade, 15)}/{formatText(lote.comprador?.estado, 2)}</td>
                <td>-</td>
                <td style={{textAlign: 'left', fontSize: '9px'}}>{formatText(lote.fazenda_comprador?.nome, 12)}</td>
              </tr>
            </tbody>
          </table>

          {/* Tabela Lotes */}
          <table className="tabela-lotes">
            <thead>
              <tr>
                <th style={{width: '8%'}}>Lote</th>
                <th style={{width: '25%'}}>Descrição</th>
                <th style={{width: '12%'}}>Raça</th>
                <th style={{width: '8%'}}>{labelCurral}</th>
                <th style={{width: '8%'}}>Qtd</th>
                <th style={{width: '10%'}}>Peso</th>
                <th style={{width: '14%'}}>Valor Unit.</th>
                <th style={{width: '15%'}}>Valor Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>{formatText(lote.numero_lote, 10)}</strong></td>
                <td style={{textAlign: 'left', paddingLeft: '12px'}}>{descricao}</td>
                <td>{raca}</td>
                <td>{formatText(lote.curral, 8)}</td>
                <td><strong>{lote.qtd}</strong></td>
                <td>{peso}</td>
                <td><strong>{formatCurrency(valorUnitario)}</strong></td>
                <td><strong style={{color: '#059669'}}>{formatCurrency(lote.valor_venda_total)}</strong></td>
              </tr>
            </tbody>
          </table>

          {/* Tabela Comissões */}
          <table className="tabela-comissoes">
            <tbody>
              {config.mostrar_comissao_venda && (
                <tr>
                  <td>Comissão de Venda</td>
                  <td>{formatPercentage(comissaoVenda)} — {formatCurrency(valorComissaoVenda)}</td>
                </tr>
              )}
              <tr>
                <td>Comissão de Compra</td>
                <td>{formatPercentage(comissaoCompra)} — {formatCurrency(valorComissaoCompra)}</td>
              </tr>
              <tr>
                <td>Despesas</td>
                <td>{formatCurrency(0)}</td>
              </tr>
              <tr>
                <td>Somatório Comissão</td>
                <td>{formatCurrency(somaComissoes)}</td>
              </tr>
              <tr>
                <td><strong>Valor Total</strong></td>
                <td><strong>{formatCurrency(valorTotal)}</strong></td>
              </tr>
              <tr>
                <td>Valor Total + Comissão de Compra</td>
                <td>{formatCurrency(valorTotalComComissao)}</td>
              </tr>
              <tr>
                <td><strong>Valor Líquido</strong></td>
                <td><strong>{formatCurrency(valorTotal)}</strong></td>
              </tr>
            </tbody>
          </table>

          {/* Tabela Parcelas */}
          <table className="tabela-parcelas">
            <thead>
              <tr>
                <th>Parcela</th>
                <th>Vencimento</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {parcelas.length > 0 ? (
                parcelas.map(parcela => (
                  <tr key={parcela.numero}>
                    <td>{parcela.numero}</td>
                    <td>{formatDate(parcela.data)}</td>
                    <td>{formatCurrency(parcela.valor)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td>1</td>
                  <td>{formatDate(new Date())}</td>
                  <td>{formatCurrency(lote.valor_venda_total)}</td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Dados Bancários */}
          {config.mostrar_dados_bancarios_vendedor && lote.vendedor?.dados_bancarios?.length > 0 && (() => {
            const dadosPrincipais = lote.vendedor.dados_bancarios.find(d => d.is_principal) || lote.vendedor.dados_bancarios[0];
            return (
              <div style={{display: 'flex', gap: '8px', marginBottom: '10px'}}>
                <div style={{flex: 1, border: '1px solid #000', padding: '8px', fontSize: '12px', fontWeight: '500', lineHeight: '1.3'}}>
                  <strong>DADOS BANCÁRIOS (VENDEDOR):</strong> Banco: <strong>{dadosPrincipais?.banco || '-'}</strong> • Agência: <strong>{dadosPrincipais?.agencia || '-'}</strong> • Conta: <strong>{dadosPrincipais?.conta || '-'}</strong> • PIX: <strong>{dadosPrincipais?.pix || '-'}</strong>
                </div>
              </div>
            );
          })()}
          {config.mostrar_dados_bancarios_organizador && organizadorAtivo?.dados_bancarios?.length > 0 && (() => {
            const dadosPrincipais = organizadorAtivo.dados_bancarios.find(d => d.is_principal) || organizadorAtivo.dados_bancarios[0];
            return (
              <div style={{display: 'flex', gap: '8px', marginBottom: '10px'}}>
                <div style={{flex: 1, border: '1px solid #000', padding: '8px', fontSize: '12px', fontWeight: '500', lineHeight: '1.3'}}>
                  <strong>DADOS BANCÁRIOS (ORGANIZADOR):</strong> Banco: <strong>{dadosPrincipais?.banco || '-'}</strong> • Agência: <strong>{dadosPrincipais?.agencia || '-'}</strong> • Conta: <strong>{dadosPrincipais?.conta || '-'}</strong> • PIX: <strong>{dadosPrincipais?.pix || '-'}</strong>
                </div>
              </div>
            );
          })()}

          {/* Observações */}
          <div className="observacoes">
            <h3>OBSERVAÇÕES CONTRATUAIS</h3>
            <div className="clausulas" dangerouslySetInnerHTML={{__html: processarClausulas(config.clausulas)}} />
          </div>

          {/* Rodapé */}
          <div className="rodape">
            <div className="data-local">Patos de Minas – MG, {formatDate(leilao.data_leilao)}</div>
            <div className="assinaturas">
              <div className="assinatura">
                <div className="linha"></div>
                <div className="nome">Vendedor</div>
                <div style={{fontSize: '9px', color: '#6b7280', marginTop: '4px'}}>Assinatura e CPF/CNPJ</div>
              </div>
              <div className="assinatura">
                <div className="linha"></div>
                <div className="nome">Comprador</div>
                <div style={{fontSize: '9px', color: '#6b7280', marginTop: '4px'}}>Assinatura e CPF/CNPJ</div>
              </div>
              <div className="assinatura">
                <div className="linha"></div>
                <div className="nome">Testemunha</div>
                <div style={{fontSize: '9px', color: '#6b7280', marginTop: '4px'}}>Assinatura e CPF</div>
              </div>
            </div>
            <div className="site">
              <div style={{marginBottom: '5px'}}>www.rematei.com.br</div>
              <div style={{fontSize: '8px', color: '#9ca3af'}}>Sistema de Gestão de Leilões - Todos os direitos reservados</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContratoUmaVia;